import IconEmoji from '@assets/images/common/icon/channel/chat-menu-emoji.svg';
import IconPin from '@assets/images/common/icon/channel/chat-menu-pin.svg';
import IconPinStroke from '@assets/images/common/icon/channel/chat-menu-pin-stroke.svg';
import IconLink from '@assets/images/common/icon/editor/icon_link.svg';
import MoreVertIcon from '@assets/images/common/icon/more-vert/more-vert.svg';
import IconTransfer from '@assets/images/common/icon/transfer/transfer.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  ChatReaction,
  chatReactionApiClient,
} from '@src/api/shared/community/chatReactionApiClient';
import { DEFAULT_REACTIONS } from '@src/constants/defaultReactions';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { useReactionContext } from '@src/context/ReactionContext';
import EmojiDropdownMenuPc from '@src/features/shared/community/components/chat/EmojiDropdownMenuPc';
import { useCanOpenThread } from '@src/features/shared/community/components/chat/hooks/useCanOpenThread';
import Tooltip from '@src/features/shared/community/components/chat/Tooltip';
import { useDropdownPosition } from '@src/hooks/useDropdownPosition';
import { useReactionLoginAlertTooltip } from '@src/hooks/useReactionLoginAlertTooltip';
import { isTenantMember } from '@src/utils/checkTenantMemberPermission';
import { Emoji, EmojiClickData } from 'emoji-picker-react';
import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TOOLTIP_DISPLAY_TIME } from './chatList/ChatItemHeader';
import { ChatMenuPermission } from './ChatMessage';

type ChatActionMenuPcProps = {
  chatId: number;
  handlePinClick: () => void;
  handleChatLinkCopyClick: () => void;
  handleChatEditClick: () => void;
  handleChatDeleteClick: () => void;
  handleChatReportClick: () => void;
  handleOpenThread?: () => void;
  isPinned: boolean;
  chatMenuPermission?: ChatMenuPermission;
  currentReactions?: ChatReaction[];
};

const preventDragHandler = (e: React.DragEvent<HTMLElement>) => {
  e.preventDefault();
};

const ChatActionMenuPc = ({
  chatId,
  handlePinClick,
  handleChatLinkCopyClick,
  handleChatEditClick,
  handleChatDeleteClick,
  handleChatReportClick,
  handleOpenThread,
  isPinned,
  chatMenuPermission,
  currentReactions,
}: ChatActionMenuPcProps) => {
  const { code } = useParams<{ code: string }>();
  const { reactions, updateReactionUsers, updateReactionsOptimistically } = useReactionContext();
  const actionMenuParentRef = useRef<HTMLDivElement>(null);
  const [isKebabMenuOpen, setIsKebabMenuOpen] = useState(false);
  const [isEmojiMenuOpen, setIsEmojiMenuOpen] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [isThreadTooltipVisible, setIsThreadTooltipVisible] = useState(false);
  const [isThreadTooltipBelow, setIsThreadTooltipBelow] = useState(false);
  const [isCopyTooltipVisible, setIsCopyTooltipVisible] = useState(false);
  const [isCopyTooltipBelow, setIsCopyTooltipBelow] = useState(false);
  const copyTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { profile } = useSharedAuth();
  const isTenantAdminUser = profile?.isTenant && profile?.tenantProfile?.user?.isAdmin;
  const isUserTenantMember = isTenantMember(profile);
  const canOpenThread = useCanOpenThread();

  const { isTooltipVisible, showTooltipIfNotLoggedIn, hideTooltip } =
    useReactionLoginAlertTooltip();
  const [showTenantMemberTooltip, setShowTenantMemberTooltip] = useState(false);

  const {
    position: emojiPosition,
    below: menuPositionBelow,
    calculatePosition,
  } = useDropdownPosition(460, 5);

  const calculateTooltipPosition = () => {
    if (!actionMenuParentRef.current) {
      return false;
    }

    const rect = actionMenuParentRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const upperThreshold = windowHeight / 5;

    // アクションメニューが画面上部5分の1にある場合は下側に表示
    return rect.top < upperThreshold;
  };

  const handleLinkCopyClick = () => {
    handleChatLinkCopyClick();
    setIsCopyTooltipBelow(calculateTooltipPosition());
    setIsCopyTooltipVisible(true);
    if (copyTooltipTimerRef.current !== null) {
      clearTimeout(copyTooltipTimerRef.current);
    }
    copyTooltipTimerRef.current = setTimeout(() => setIsCopyTooltipVisible(false), TOOLTIP_DISPLAY_TIME);
  };

  const handleOpenEmojiClick = () => {
    calculatePosition(actionMenuParentRef);

    if (isUserTenantMember) {
      setShowTenantMemberTooltip(true);
      setTimeout(() => {
        setShowTenantMemberTooltip(false);
      }, TOOLTIP_DISPLAY_TIME);
      return;
    }

    const shouldOpenEmojiMenu = !showTooltipIfNotLoggedIn();

    setIsOverlayOpen(shouldOpenEmojiMenu);
    setIsEmojiMenuOpen(shouldOpenEmojiMenu);
    setIsKebabMenuOpen(false);
  };

  const handleKebabClick = () => {
    setIsOverlayOpen(!isOverlayOpen);
    setIsKebabMenuOpen(!isKebabMenuOpen);
    setIsEmojiMenuOpen(false);
    hideTooltip();
  };

  const handleOverlayClick = () => {
    setIsOverlayOpen(false);
    setIsKebabMenuOpen(false);
    setIsEmojiMenuOpen(false);
    hideTooltip();
  };

  const handleEmojiSelect = useCallback(
    async (emojiObject: EmojiClickData) => {
      if (!code || !chatId) {
        return;
      }

      if (isUserTenantMember) {
        setShowTenantMemberTooltip(true);
        setTimeout(() => {
          setShowTenantMemberTooltip(false);
        }, TOOLTIP_DISPLAY_TIME);
        return;
      }

      const shouldOpenEmojiMenu = !showTooltipIfNotLoggedIn();
      if (!shouldOpenEmojiMenu) {
        return;
      }

      const emoji = emojiObject.unified.toLowerCase();

      setIsEmojiMenuOpen(false);
      setIsOverlayOpen(false);

      // 楽観更新: 即座にリアクションを更新
      const reactionsToUse = reactions[chatId] || currentReactions;
      updateReactionsOptimistically(chatId, emoji, reactionsToUse);

      // APIリクエストは非同期で実行
      const res = await chatReactionApiClient.post(code, chatId, { emoji });
      handleApiResponse(res, (data) => {
        if (data) {
          // ユーザー情報のみを更新
          updateReactionUsers(chatId, emoji, data.users);
        }
      });
    },
    [
      isUserTenantMember,
      chatId,
      code,
      currentReactions,
      reactions,
      showTooltipIfNotLoggedIn,
      updateReactionUsers,
      updateReactionsOptimistically,
    ],
  );

  const IconButton = ({
    icon,
    onClick,
    onMouseEnter,
    onMouseLeave,
  }: {
    icon: string;
    onClick: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
  }) => {
    return (
      <button
        className='size-[24px]'
        type='button'
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragStart={preventDragHandler}
      >
        <img
          className='size-full'
          src={icon}
          width='24'
          height='24'
          alt=''
          onDragStart={preventDragHandler}
        />
      </button>
    );
  };

  const KebabInMenuButton = ({ label, onClick }: { label: string; onClick: () => void }) => {
    return (
      <button
        className='block select-none bg-white px-6 py-3 text-left text-sm transition-colors hover:bg-primary-500 hover:text-white'
        type='button'
        onClick={(e) => {
          e.stopPropagation();
          onClick();
          setIsKebabMenuOpen(false);
        }}
        onDragStart={preventDragHandler}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      {isOverlayOpen && (
        <div
          className='fixed inset-0 z-[99999] bg-transparent'
          onClick={handleOverlayClick}
        />
      )}
      <div
        className='flex items-center gap-[6px] rounded-[4px] border border-solid border-gray-200 bg-white px-2 py-1'
        ref={actionMenuParentRef}
      >
        {DEFAULT_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            className='grid size-[24px] place-items-center text-[18px] leading-none'
            type='button'
            onClick={() => handleEmojiSelect({ unified: emoji.toLowerCase() } as EmojiClickData)}
            onDragStart={preventDragHandler}
          >
            <Emoji
              key={emoji}
              unified={emoji.toLowerCase()}
              size={21}
            />
          </button>
        ))}
        <IconButton
          icon={IconEmoji}
          onClick={handleOpenEmojiClick}
        />
        {handleOpenThread && canOpenThread && (
          <div className='relative mb-[-5px]'>
            <IconButton
              icon={IconTransfer}
              onClick={handleOpenThread}
              onMouseEnter={() => {
                setIsThreadTooltipBelow(calculateTooltipPosition());
                setIsThreadTooltipVisible(true);
              }}
              onMouseLeave={() => setIsThreadTooltipVisible(false)}
            />
            {isThreadTooltipVisible && (
              <div
                className={`absolute right-[-54px] z-[9999999] whitespace-nowrap text-[12px] ${isThreadTooltipBelow ? 'top-full mt-3' : 'bottom-full mb-3'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip
                  label='スレッドに返信する'
                  weight='500'
                />
              </div>
            )}
          </div>
        )}
        {isTenantAdminUser && (
          <IconButton
            icon={isPinned ? IconPin : IconPinStroke}
            onClick={handlePinClick}
          />
        )}
        <div className='relative flex items-center'>
          <IconButton
            icon={IconLink}
            onClick={handleLinkCopyClick}
          />
          {isCopyTooltipVisible && (
            <div
              className={`absolute left-1/2 z-[9999999] -translate-x-1/2 whitespace-nowrap text-[12px] ${isCopyTooltipBelow ? 'top-full mt-3' : 'bottom-full mb-3'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip
                label='コピーしました'
                weight='500'
              />
            </div>
          )}
        </div>
        {chatMenuPermission?.canShowKebabMenu && (
          <button
            className='size-[24px]'
            type='button'
            onClick={handleKebabClick}
            onDragStart={preventDragHandler}
          >
            <img
              className='size-full hover:opacity-60'
              src={MoreVertIcon}
              width='24'
              height='24'
              alt=''
            />
          </button>
        )}
        {isKebabMenuOpen && (
          <div
            className={`absolute right-0 z-[9999999] w-[114px] ${menuPositionBelow ? 'top-full mt-1' : 'bottom-full mb-1'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex flex-col gap-px overflow-hidden rounded border border-solid border-gray-200 bg-gray-200'>
              {chatMenuPermission?.canEdit && (
                <KebabInMenuButton
                  label='編集する'
                  onClick={() => {
                    setIsOverlayOpen(false);
                    handleChatEditClick();
                  }}
                />
              )}
              {chatMenuPermission?.canDelete && (
                <KebabInMenuButton
                  label='削除'
                  onClick={() => {
                    setIsOverlayOpen(false);
                    handleChatDeleteClick();
                  }}
                />
              )}
              {chatMenuPermission?.canReport && (
                <KebabInMenuButton
                  label='報告する'
                  onClick={() => {
                    setIsOverlayOpen(false);
                    handleChatReportClick();
                  }}
                />
              )}
            </div>
          </div>
        )}
        {isEmojiMenuOpen && (
          <EmojiDropdownMenuPc
            onEmojiClick={handleEmojiSelect}
            position={emojiPosition}
            menuPositionBelow={menuPositionBelow}
            horizontalAlign='right'
          />
        )}
        {showTenantMemberTooltip && (
          <div
            className={`absolute right-0 z-[9999999] whitespace-nowrap text-[12px] ${menuPositionBelow ? 'top-full mt-1' : 'bottom-full mb-1'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip
              label='リアクション権限がありません。管理者にお問い合わせください。'
              weight='500'
            />
          </div>
        )}
        {isTooltipVisible && (
          <div
            className={`absolute right-0 z-[9999999] whitespace-nowrap text-[12px] ${menuPositionBelow ? 'top-full mt-1' : 'bottom-full mb-1'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip
              label='リアクションを利用するには、ログインしてください。'
              weight='500'
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ChatActionMenuPc;
